<script setup lang="ts">
import { TableOfContentsPlugin, useLexicalComposer } from "lexical-vue";
import { computed, onMounted, ref } from "vue";

import Button from "~/components/dumb/Button.vue";
import { colorsByTheme } from "~/constants/style";
import { ButtonStyle } from "~/shared/enums";
import { usePageStore } from "~/stores";

const TAG_TO_WIDTH = new Map([
  ["h1", "w-4"],
  ["h2", "w-3"],
  ["h3", "w-2"],
]);
const TAG_TO_MARGIN = new Map([
  ["h2", "ml-3"],
  ["h3", "ml-6"],
]);

const editor = useLexicalComposer();
const pageStore = usePageStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);

const display = ref(false);

const scrollTo = (key: string) => {
  const element = editor.getElementByKey(key);
  const scroller = element?.closest(".dart-doc-scroll");
  if (!scroller || !element) {
    return;
  }
  const scrollerRect = scroller.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  scroller.scrollBy({
    top: elementRect.top - scrollerRect.top - 16,
    behavior: "smooth",
  });
};

onMounted(() => {
  display.value = true;
});
</script>

<template>
  <Teleport v-if="display" to=".dart-doc-container">
    <TableOfContentsPlugin v-slot="{ tableOfContents: toc }">
      <div
        v-if="toc.length > 0"
        class="pointer-events-none absolute inset-y-0 right-5 flex max-h-full flex-col justify-around overflow-y-hidden">
        <div class="group/toc pointer-events-auto max-h-full py-6 pr-2 pl-6">
          <div class="flex w-6 flex-col items-end gap-2 py-2 pl-2 group-hover/toc:hidden">
            <div
              v-for="tocElem in toc"
              :key="tocElem[0]"
              class="h-0.5 rounded-sm bg-hvy"
              :class="TAG_TO_WIDTH.get(tocElem[2])" />
          </div>
          <div
            class="hidden max-h-full w-60 flex-col overflow-y-scroll rounded-lg bg-lt p-2 group-hover/toc:flex"
            :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }">
            <Button
              v-for="tocElem in toc"
              :key="tocElem[0]"
              :text="tocElem[1]"
              :btn-style="ButtonStyle.SECONDARY"
              break-words
              borderless
              a11y-label="Header"
              text-style="line-clamp-3"
              class="min-h-[30px] shrink-0 !justify-start text-left hover:bg-md"
              :class="TAG_TO_MARGIN.get(tocElem[2])"
              @click="scrollTo(tocElem[0])" />
          </div>
        </div>
        <div />
      </div>
    </TableOfContentsPlugin>
  </Teleport>
</template>
